import { Lane, TargetSource, TargetType, TrackedTarget, TurnType } from "@app/shared";
import cn from "classnames";
import _ from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { IconLink, Icons } from "../../../shared";
import { DataViewerProps } from "../DataViewer";
import { getRadarFrame, getTargetsByFrame, getVideoFrame, ProjectWithData } from "../ProjectWithData";
import { getTrackedTargetHistory } from "../TargetHelpers";
import { UserTarget } from "./UserTarget";

var userIdCount = 0;

export const UserResultsViewer: React.FC<DataViewerProps> = ({ setProject, setNeedsVideoClick, onFrameChange, ...props }) => {

    const [currentFrame, setCurrentFrame] = useState({ radar: 0, video: 0 });
    const [scrollRadarId, setScrollRadarId] = useState<string>();

    // Refs for scrolling
    const containerRef = useRef<HTMLDivElement>(null);
    const targetRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const targetToReposition = useRef<TrackedTarget | undefined>(undefined);

    //Get the current frame number based on the current time
    useEffect(() => {
        const radarFrame = getRadarFrame(props.project, props.currentTimeS);
        const videoFrame = getVideoFrame(props.project, props.currentTimeS);
        setCurrentFrame({ radar: radarFrame, video: videoFrame });
        //Set the target of the ID we should be scrolling to
        const lastTargetId = props.project.simTargetsByFrame?.[radarFrame]?.[0]?.targetId;
        if (lastTargetId && lastTargetId !== scrollRadarId) {
            setScrollRadarId(lastTargetId);
        }
    }, [props.currentTimeS, props.project]); // eslint-disable-line react-hooks/exhaustive-deps

    //When our radarID changes, scroll to the target
    useEffect(() => {
        if (scrollRadarId)
            targetRefs.current[scrollRadarId]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [scrollRadarId]);

    const handleAddTargetOnClick = useCallback(() => {
        userIdCount++;
        var newTarget: TrackedTarget = {
            targetId: `u${userIdCount}`,
            radarId: userIdCount,
            targetHistory: [],
            firstFrameTracked: currentFrame.video,
            lastFrameTracked: currentFrame.video,
            position: { x: 0, y: 0, z: 0 },
            velocity: { x: 0, y: 0, z: 0 },
            acceleration: { x: 0, y: 0, z: 0 },
            maxLength: 0,
            maxWidth: 0,
            hasPhoto: false,
            laneNumber: Lane.Unknown,
            turn: TurnType.Straight,
            source: TargetSource.Camera,
            lastEventCheck: "",
            events: [],
            reportedType: TargetType.Unknown,
        };
        console.log("HandleAddTargetOnClick", newTarget);

        setProject((oldProject) => {
            if (!oldProject) return oldProject;

            const userResults = _.cloneDeep(oldProject.simResults) ?? { events: [], targets: {} };
            const projectFrame = oldProject.projectFrameType === "video" ? currentFrame.video : currentFrame.radar;
            if (!userResults.targets[projectFrame])
                userResults.targets[projectFrame] = [];

            userResults.targets[projectFrame].push(newTarget as TrackedTarget);
            const userTargetsByFrame = getTargetsByFrame(userResults, oldProject as ProjectWithData);
            const userTargets = _.sortBy(Object.values(userResults?.targets ?? []).flat(), (o) => o.firstFrameTracked);

            return Object.assign(new ProjectWithData(), { ...oldProject, userResults, userTargets, userTargetsByFrame } as Partial<ProjectWithData>);
        });//, true); //TODO: Add back in hasChange checking
    }, [setProject, currentFrame]);

    const handleTargetOnClick = useCallback((target: TrackedTarget) => {
        setScrollRadarId(target.targetId);
        onFrameChange(.05 + ((props.project.radarStartDate?.getTime() ?? 0) - (props.project.getDataStartDate()?.getTime() ?? 0)) / 1000 + (target.firstFrameTracked) / (props.project.radarFps ?? 10));
    }, [setScrollRadarId, props.project, onFrameChange]);

    const handleCopyFromSimOnClick = useCallback(() => {
        userIdCount = 0;
        setProject((oldProject) => {
            if (!oldProject) return oldProject;
            const userResults = _.cloneDeep(oldProject.simResults);
            const userTargetsByFrame = getTargetsByFrame(userResults, props.project);
            const userTargets = Object.values(userResults?.targets ?? []).flat();

            return Object.assign(new ProjectWithData(), { ...oldProject, userResults, userTargets, userTargetsByFrame } as Partial<ProjectWithData>);
        })
    }, [props.project, setProject]);

    const handleDeleteOnClick = useCallback(() => {
        userIdCount = 0;
        setProject((oldProject) => (oldProject ?
            Object.assign(new ProjectWithData(), { ...oldProject, userResults: undefined, userTargets: undefined, userTargetsByFrame: undefined } as Partial<ProjectWithData>) : new ProjectWithData()));
    }, [setProject]);

    const handleTargetOnChange = useCallback((oldTarget: TrackedTarget, newTarget: TrackedTarget) => {
        setProject((oldProject) => {
            if (!oldProject) return new ProjectWithData();

            //Make sure to assign to the old target, so it updates the reference in the targets by frame, etc
            const newTargets = oldProject.userTargets?.map(t => t === oldTarget ? Object.assign(oldTarget, newTarget) : t);
            return Object.assign(new ProjectWithData(), { ...oldProject, userTargets: newTargets } as Partial<ProjectWithData>);
        });
    }, [setProject]);

    const handleTargetDeleteOnClick = useCallback((target: TrackedTarget) => {
        setProject((oldProject) => {
            if (!oldProject) return new ProjectWithData();

            const userResults = oldProject.userResults;
            Object.keys(userResults?.targets ?? []).forEach((key) => {
                userResults!.targets[key as any] = userResults!.targets[key as any].filter((t) => t !== target)
            });

            //Update other arrays that reference these results
            const userTargetsByFrame = getTargetsByFrame(userResults, props.project);
            const userTargets = Object.values(userResults?.targets ?? []).flat();

            return Object.assign(new ProjectWithData(), { ...oldProject, userTargets, userTargetsByFrame, userResults } as Partial<ProjectWithData>);
        });
    }, [setProject, props.project]);

    const handleMergeTargetsOnClick = useCallback((targetA?: TrackedTarget, targetB?: TrackedTarget) => {
        if (!targetA || !targetB) return;
        targetA.lastFrameTracked = targetB.lastFrameTracked;
        targetA.targetHistory.push(...targetB.targetHistory);
        handleTargetDeleteOnClick(targetB);
    }, [handleTargetDeleteOnClick]);

    const handleTargetNeedsVideoClick = useCallback((needsClick: boolean, target: TrackedTarget) => {
        targetToReposition.current = needsClick ? target : undefined;
        setNeedsVideoClick?.(needsClick);
    }, [setNeedsVideoClick, targetToReposition]);

    //When we get a video click, adjust the position of the target
    useEffect(() => {
        if (!props.lastVideoClick || !targetToReposition.current) return;

        const hh = props.project.pointTranslator;
        if (!hh) return;
        const radarPoint = hh.imageToGround(props.lastVideoClick);

        var currentHistory = getTrackedTargetHistory(targetToReposition.current, currentFrame);

        if (currentHistory) {
            currentHistory.position = {
                x: radarPoint.x,
                y: radarPoint.y,
                z: 0,
            };
            return;
        }

        //We need to add a new TargetHistory entry to the target
        targetToReposition.current.targetHistory.push({
            targetId: targetToReposition.current.targetId,
            frameNumber: targetToReposition.current.source === TargetSource.Radar ? currentFrame.radar : currentFrame.video,
            position: {
                x: radarPoint.x,
                y: radarPoint.y,
                z: 0,
            },
            velocity: { x: 0, y: 0, z: 0 },
            acceleration: { x: 0, y: 0, z: 0 },
            pointCount: 1,
        });

        setNeedsVideoClick?.(false);
    }, [props.lastVideoClick, setNeedsVideoClick]); // eslint-disable-line react-hooks/exhaustive-deps

    return (<>
        {props.project.userResults &&
            <div className="border-b border-separators-page p-1 m-0 font-small text-small text-sm flex">
                <div className="flex-grow gap-3">
                    0% Count, 0% Class
                </div>
                <div className="flex gap-5">
                    <IconLink onClick={handleAddTargetOnClick} Icon={Icons.PlusCircle}>Add Target </IconLink>
                    <IconLink onClick={handleDeleteOnClick} Icon={Icons.Trash} iconSize={16}>Clear All</IconLink>
                </div>
            </div>
        }
        {!props.project.userResults &&
            <div className="flex flex-col gap-3 text-center justify-center place-content-center place-items-center p-5">
                {props.project.simResults &&
                    <IconLink onClick={handleCopyFromSimOnClick} Icon={Icons.JsonFile} iconSize={20}>Copy From Simulation Results</IconLink>
                }
            </div>
        }
        {props.project.userResults &&
            <div
                ref={containerRef}
                style={{
                    minWidth: 600,
                    maxHeight: 700,
                    overflowY: "auto",

                    padding: 8,
                }}
            >
                <div>
                    {props.project.userTargets && props.project.userTargets.map((target, i) => {
                        const prevTarget = props.project.userTargets?.[i - 1];
                        const targetFrame = target.source === TargetSource.Radar ? currentFrame.radar : currentFrame.video;
                        const prevTargetFrame = prevTarget?.source === TargetSource.Radar ? currentFrame.radar : currentFrame.video;

                        return <div
                            key={target.targetId}
                            ref={el => (targetRefs.current[target.targetId] = el)}
                        >
                            <div className={cn(
                                { "bg-background-bright-blue ": (prevTarget?.lastFrameTracked ?? 0) < prevTargetFrame && target.firstFrameTracked > targetFrame },
                                "h-5px hover:h-40px overflow-hidden group "
                            )}>
                                <div className="hidden group-hover:block p-2 text-center">
                                    <div className="flex flex-row gap-5 justify-center place-content-center place-items-center">
                                        {i > 0 &&
                                            <IconLink onClick={() => handleMergeTargetsOnClick(prevTarget, target)} Icon={Icons.MergeVertical}>Merge</IconLink>
                                        }
                                    </div>
                                </div>
                            </div>
                            <UserTarget
                                setNeedsVideoClick={(needsClick) => handleTargetNeedsVideoClick(needsClick, target)}
                                needsVideoClick={props.needsVideoClick}
                                key={target.targetId}
                                target={target}
                                currentFrame={currentFrame}
                                lastVideoClick={props.lastVideoClick}
                                onClick={handleTargetOnClick}
                                onChanged={(newTarget: TrackedTarget) => handleTargetOnChange(target, newTarget)}
                                onDelete={handleTargetDeleteOnClick}
                            />
                        </div>
                    })
                    }

                </div>
            </div>
        }
    </>
    );
}